import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Ease } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * Mamut de ceniza (models/mammoth.ts): idle and walk (loops), attack (it tosses its head up and drives the tusks
 * down into the wall, landing at ~0.13 s when the simulation deals the blow, then grinds and wrenches them free),
 * hit, stun (loop), die (the fore knees buckle, then the rear, and it slumps onto its side; the view then sinks
 * it) and `panic`, when fire touches it: it rears on its hind legs, ears flared and trunk thrown up to trumpet,
 * paws the air and crashes back down. Everything is slow and enormous; the trunk, the ears, the tail and the
 * wrecked howdah trail every move.
 *
 * Conventions: limbs and the trunk hang along -Y, so a negative X swings them forward (and, far enough, up); +X on
 * a lower leg folds the knee back; +X on the body pitches it nose-down about the hind hips (−X rears); +X on the
 * head drops the brow; the L bones sit on −X. +Y on earL and −Y on earR flare
 * the ears out.
 */

type B = 'root' | 'body' | 'head' | 'earL' | 'earR' | 'trunk1' | 'trunk2' | 'trunk3' | 'howdah' | 'tail'
  | 'foreL' | 'foreL2' | 'foreR' | 'foreR2' | 'hindL' | 'hindL2' | 'hindR' | 'hindR2';
type P = KeyPose<B>;

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Whole-figure squash from impulses: a landing flattens it a hair and it springs back. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** A deterministic shiver (two sines at integer multiples of `f`). */
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 2 * t + 1.3);

/** Adds the stance to every beat (so a timeline's beats only spell what changes from it). */
const over = (st: P, beats: readonly (readonly [number, P, Ease?])[]): Beat<B>[] =>
  beats.map(([t, p, e]) => (e ? [t, { ...st, ...p }, e] : [t, { ...st, ...p }]) as Beat<B>);

/** Ears out by `k` (radians each). */
const ears = (b: Bones<B>, k: number): void => {
  b.earL.rot(0, k, 0);
  b.earR.rot(0, -k, 0);
};

/** Head a touch low, the trunk hanging with a slight curl, the tail down. */
const ST: P = {
  head: [0.04, 0, 0],
  trunk1: [-0.04, 0, 0],
  trunk2: [-0.06, 0, 0],
  trunk3: [-0.18, 0, 0],
  tail: [0.12, 0, 0],
};

/** The trunk, ears, tail and howdah trail the body. */
const LAG: Partial<Record<B, number>> = { head: 0.03, trunk1: 0.05, trunk2: 0.08, trunk3: 0.11, earL: 0.04, earR: 0.04, tail: 0.07, howdah: 0.06 };

/** It breathes slow and deep, the trunk swaying and curling, the ears fanning, the head swinging to look. */
const idle = (): Clip<B> =>
  clip<B>('idle', 4.8, true, (b, t) => {
    const p = phase(t, 4.8) * TAU;
    apply(b, ST);
    const br = Math.sin(p * 2);
    b.body.rot(-0.008 * br, 0, 0.01 * Math.sin(p)).scale(1 + 0.006 * br);
    b.root.pos(0.01 * Math.sin(p), -0.008 * (1 + br), 0);
    b.head.rot(0.03 * Math.sin(p + 0.6), 0.14 * Math.sin(p), 0.03 * Math.sin(p + 1));
    // The trunk swings like a pendulum, and twice a loop the tip curls up and lets go.
    const curl = Math.max(0, Math.sin(p * 2 - 0.5)) ** 3;
    b.trunk1.rot(0.05 * Math.sin(p + 0.4), 0, 0.06 * Math.sin(p - 0.3));
    b.trunk2.rot(0.06 * Math.sin(p - 0.1) - 0.2 * curl, 0, 0.08 * Math.sin(p - 0.8));
    b.trunk3.rot(0.08 * Math.sin(p - 0.6) - 0.45 * curl, 0, 0.1 * Math.sin(p - 1.3));
    ears(b, 0.08 + 0.1 * Math.max(0, Math.sin(p * 3)) ** 2);
    b.tail.rot(0, 0, 0.2 * Math.sin(p * 2 + 0.5));
    b.howdah.rot(0.01 * Math.sin(p * 2 - 0.6), 0, 0.012 * Math.sin(p - 0.5));
    // A weight shift: one fore foot lifts and settles.
    const lift = Math.max(0, Math.sin(p - 2)) ** 8;
    b.foreR.rot(-0.08 * lift);
    b.foreR2.rot(0.25 * lift);
  }, (t) => {
    const br = Math.sin(phase(t, 4.8) * TAU * 2);
    return [1 + 0.006 * br, 1 - 0.005 * br, 1 + 0.006 * br];
  });

/**
 * Four-beat, slow and heavy: hind left, fore left, hind right, fore right a quarter apart. Each foot comes down
 * with its weight (the body drops a hair), the barrel rolls over the planted side, the head nods, the trunk
 * swings in a lagging pendulum and the wreck on its back sways and creaks.
 */
const walk = (dur = 1.9): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    apply(b, ST, 0.8);
    const leg = (up: B, lo: B, off: number, A: number, fold: number): void => {
      const a = th - off * TAU;
      b[up].rot(-A * Math.sin(a) - 0.02);
      b[lo].rot(fold * Math.max(0, Math.cos(a)) ** 1.5);
    };
    leg('hindL', 'hindL2', 0, 0.26, 0.42);
    leg('foreL', 'foreL2', 0.25, 0.3, 0.55);
    leg('hindR', 'hindR2', 0.5, 0.26, 0.42);
    leg('foreR', 'foreR2', 0.75, 0.3, 0.55);
    const land = Math.max(0, -Math.cos(4 * th)) ** 3;
    b.root.pos(0.03 * Math.sin(th), -0.035 - 0.025 * land, 0).rot(0, 0, 0.025 * Math.sin(th - 0.4));
    b.body.rot(0.012 * Math.sin(2 * th + 0.5), 0.03 * Math.sin(th), 0.02 * Math.sin(th - 0.6));
    b.head.rot(0.04 * Math.sin(2 * th - 0.8) + 0.02 * land, -0.07 * Math.sin(th - 0.3), -0.03 * Math.sin(th - 0.8));
    b.trunk1.rot(0.1 * Math.sin(th - 0.9), 0, 0.1 * Math.sin(th - 1.2));
    b.trunk2.rot(0.12 * Math.sin(th - 1.4), 0, 0.12 * Math.sin(th - 1.7));
    b.trunk3.rot(0.14 * Math.sin(th - 1.9), 0, 0.14 * Math.sin(th - 2.2));
    ears(b, 0.1 + 0.05 * Math.sin(2 * th - 1));
    b.tail.rot(0.05 * Math.sin(2 * th - 1), 0, 0.22 * Math.sin(th - 1.5));
    b.howdah.rot(0.02 * Math.sin(2 * th - 1.1) + 0.015 * land, 0.015 * Math.sin(th - 0.8), 0.045 * Math.sin(th - 1.0));
  }, (t) => {
    const k = Math.max(0, -Math.cos(4 * phase(t, dur) * TAU)) ** 3;
    return [1 + 0.012 * k, 1 - 0.015 * k, 1 + 0.012 * k];
  });

/** Head up and back, trunk tucked: the blow is already coming. */
const COIL: P = {
  root: [0, 0, 0, 0, 0.02, -0.08],
  body: [-0.08, 0, 0],
  head: [-0.4, 0.08, 0],
  trunk1: [-0.55, 0, 0],
  trunk2: [-0.6, 0, 0],
  trunk3: [-0.5, 0, 0],
  earL: [0, 0.3, 0],
  earR: [0, -0.3, 0],
  foreL: [0.1, 0, 0],
  foreR: [0.06, 0, 0],
  hindL: [-0.12, 0, 0],
  hindL2: [0.15, 0, 0],
  hindR: [-0.12, 0, 0],
  hindR2: [0.15, 0, 0],
  tail: [-0.3, 0, 0],
};
/** Tusks driven down into the wall, the whole weight behind them. */
const SMASH: P = {
  root: [0, 0, 0, 0, -0.08, 0.22],
  body: [0.12, 0, 0],
  head: [0.5, -0.05, 0],
  trunk1: [0.15, 0, 0],
  trunk2: [0.35, 0, 0],
  trunk3: [0.4, 0, 0],
  earL: [0, 0.15, 0],
  earR: [0, -0.15, 0],
  foreL: [-0.28, 0, 0],
  foreL2: [0.1, 0, 0],
  foreR: [-0.2, 0, 0],
  foreR2: [0.12, 0, 0],
  hindL: [0.18, 0, 0],
  hindR: [0.2, 0, 0],
  tail: [0.2, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 1.5, false, (b, t) => {
    timeline(b, t, over(ST, [
      [0, {}],
      [0.07, COIL, outQuad],
      [0.13, SMASH, inQuad],
      // It grinds the tusks in, twisting the head one way, then wrenches them free the other.
      [0.38, { ...SMASH, head: [0.45, 0.22, 0.14], body: [0.1, 0.05, 0.02] }, outCubic],
      [0.65, { ...SMASH, root: [0, 0, 0, 0, -0.06, 0.15], head: [0.1, -0.2, -0.12], body: [0.04, -0.04, 0], trunk1: [-0.2, 0, 0], trunk2: [-0.1, 0, 0] }, inOut],
      [1.5, {}, inOut],
    ]), LAG);
    // The shock of the blow up the tusks and through the barrel; the wreck on its back jolts.
    const k = kick(t, 0.13, 4, 9);
    b.head.rot(-0.06 * k);
    b.body.rot(-0.03 * k);
    b.howdah.rot(0.08 * kick(t, 0.17, 3, 6), 0, 0.05 * kick(t, 0.19, 2.5, 6));
  }, (t) => thud(t, [[0.13, 0.04]]));

/** It barely feels it: the head tosses, the ears flare, the barrel rocks. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.2, 8) * span(t, 0, 0.04, outQuad);
    const a = Math.abs(k);
    b.body.rot(-0.04 * k, 0.03 * k, 0.03 * k);
    b.head.rot(-0.22 * k, 0.15 * k, 0.06 * k);
    ears(b, 0.1 + 0.35 * a);
    b.trunk1.rot(-0.25 * k);
    b.trunk2.rot(-0.2 * settle(t - 0.04, 2.2, 7));
    b.trunk3.rot(-0.2 * settle(t - 0.08, 2.2, 7));
    b.howdah.rot(-0.04 * settle(t - 0.05, 2, 6), 0, 0.03 * settle(t - 0.05, 2, 6));
    b.root.pos(0, -0.01 * a, -0.04 * k);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.02 * k, 1 - 0.025 * k, 1 + 0.02 * k];
  });

/** Dazed: legs braced, it sways from side to side, the head hung low and the trunk dragging. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    apply(b, ST, 0.6);
    b.root.rot(0, 0, 0.04 * Math.sin(p)).pos(0.04 * Math.sin(p), -0.05, 0);
    b.body.rot(0.06, 0.04 * Math.sin(p + 0.5), 0);
    b.head.rot(0.3 + 0.06 * Math.sin(p * 2), 0.2 * Math.sin(p + 0.8), 0.12 * Math.sin(p + 0.8));
    b.trunk1.rot(0.15, 0, 0.12 * Math.sin(p + 1.2));
    b.trunk2.rot(0.1, 0, 0.14 * Math.sin(p + 1.6));
    b.trunk3.rot(-0.1, 0, 0.16 * Math.sin(p + 2));
    ears(b, -0.05);
    b.tail.rot(0.25, 0, 0.1 * Math.sin(p + 2));
    b.howdah.rot(0, 0, 0.05 * Math.sin(p - 0.8));
    for (const [n, k] of [['foreL', -1], ['foreR', 1], ['hindL', -1], ['hindR', 1]] as const) b[n].rot(0, 0, k * 0.06);
  });

/**
 * Felled: it throws its head up with a last trumpet, the fore knees buckle and it drops onto them, the rear
 * follows, and it rolls slowly onto its side, the trunk slumped on the ground and the howdah sliding askew.
 */
const die = (): Clip<B> =>
  clip<B>('die', 2.8, false, (b, t) => {
    const knees: P = { foreL: [-0.35, 0, 0], foreL2: [1.6, 0, 0], foreR: [-0.3, 0, 0], foreR2: [1.55, 0, 0] };
    const kneel: P = { ...knees, root: [0, 0, 0, 0, -0.45, 0.1], body: [0.22, 0, 0], head: [0.3, 0.1, 0], trunk1: [0.1, 0, 0], trunk2: [0.4, 0, 0], trunk3: [0.4, 0, 0], tail: [0.2, 0, 0], hindL: [0.12, 0, 0], hindR: [0.12, 0, 0], earL: [0, -0.1, 0], earR: [0, 0.1, 0] };
    const sit: P = { ...kneel, root: [0, 0, 0, 0, -0.95, 0.05], body: [0.02, 0, 0], hindL: [-1.25, 0, -0.08], hindL2: [2.0, 0, 0], hindR: [-1.2, 0, 0.08], hindR2: [2.0, 0, 0], head: [0.4, 0.15, 0.05], trunk1: [0.05, 0, 0.1], trunk2: [0.1, 0, 0.3], trunk3: [-0.3, 0, 0.3], howdah: [0.02, 0, 0.05] };
    const flat: P = { ...sit, root: [0, 0, 0.42, -0.05, -0.92, 0.05], head: [0.45, 0.3, 0.3], trunk1: [0.05, 0, 0.35], trunk2: [-0.1, 0, 0.45], trunk3: [-0.4, 0, 0.35], earL: [0, -0.3, 0], earR: [0, 0.3, 0], tail: [0.4, 0, 0.3], howdah: [0.05, 0, 0.32], foreR: [-0.7, 0, 0.25], foreR2: [0.9, 0, 0], hindR: [-0.9, 0, 0.3], hindR2: [1.2, 0, 0] };
    timeline(b, t, [
      [0, ST],
      [0.3, { ...ST, root: [0, 0, 0, 0, 0.02, -0.05], body: [-0.12, 0, 0], head: [-0.5, 0, 0], trunk1: [-1.0, 0, 0], trunk2: [-0.9, 0, 0], trunk3: [-0.8, 0, 0], earL: [0, 0.6, 0], earR: [0, -0.6, 0], tail: [-0.4, 0, 0], foreL: [-0.15, 0, 0], foreR: [-0.1, 0, 0] }, outExpo],
      [0.65, { ...ST, body: [0.04, 0, 0], head: [0.1, 0, 0], foreL: [-0.15, 0, 0], foreL2: [0.4, 0, 0], foreR: [-0.15, 0, 0], foreR2: [0.4, 0, 0], root: [0, 0, 0, 0, -0.1, 0.02] }, inOut],
      [0.95, kneel, inQuad],
      [1.08, { ...kneel, root: [0, 0, 0, 0, -0.43, 0.1], head: [0.38, 0.1, 0] }, outQuad],
      [1.55, sit, inQuad],
      [1.7, { ...sit, root: [0, 0, 0, 0, -0.93, 0.05] }, outQuad],
      [2.45, flat, inOut],
      [2.8, { ...flat, root: [0, 0, 0.4, -0.05, -0.92, 0.05] }, outBack],
    ], LAG);
  }, (t) => thud(t, [[0.95, 0.05], [1.55, 0.05], [2.45, 0.03]]));

/**
 * Fire: it rears up on its hind legs with the trunk thrown up to trumpet and the ears flared wide, paws the air,
 * shaking, and crashes back down onto its fore feet, the howdah lurching, then swings its head to turn away.
 */
const panic = (): Clip<B> =>
  clip<B>('panic', 1.7, false, (b, t) => {
    const rear: P = {
      root: [0, 0, 0, 0, 0.04, -0.12], body: [-0.42, 0, 0], head: [-0.3, 0, 0],
      trunk1: [-1.3, 0, 0], trunk2: [-0.9, 0, 0], trunk3: [-0.7, 0, 0], earL: [0, 0.75, 0], earR: [0, -0.75, 0],
      foreL: [-0.55, 0, 0], foreL2: [0.9, 0, 0], foreR: [-0.35, 0, 0], foreR2: [1.1, 0, 0],
      hindL: [0.28, 0, 0], hindL2: [0.12, 0, 0], hindR: [0.3, 0, 0], hindR2: [0.12, 0, 0],
      tail: [-0.7, 0, 0], howdah: [0.12, 0, 0],
    };
    timeline(b, t, over(ST, [
      [0, {}],
      [0.12, { root: [0, 0, 0, 0, -0.05, 0], body: [0.06, 0, 0], head: [0.15, 0, 0], foreL2: [0.2, 0, 0], foreR2: [0.2, 0, 0], earL: [0, 0.4, 0], earR: [0, -0.4, 0] }, outQuad],
      [0.4, rear, outCubic],
      [0.85, { ...rear, body: [-0.38, 0, 0], foreL: [-0.35, 0, 0], foreR: [-0.6, 0, 0], foreL2: [1.1, 0, 0], foreR2: [0.8, 0, 0] }, inOut],
      [1.05, { root: [0, 0, 0, 0, -0.07, 0.06], body: [0.08, 0, 0], head: [0.2, 0.3, 0.1], trunk1: [0.2, 0, 0], trunk2: [0.2, 0, 0], foreL: [-0.2, 0, 0], foreR: [-0.15, 0, 0], foreL2: [0.05, 0, 0], foreR2: [0.05, 0, 0], earL: [0, 0.5, 0], earR: [0, -0.5, 0], tail: [-0.3, 0, 0], howdah: [-0.08, 0, 0.05] }, inQuad],
      [1.7, { head: [0.05, -0.25, -0.05], earL: [0, 0.3, 0], earR: [0, -0.3, 0] }, inOut],
    ]), LAG);
    // It trumpets, shaking from trunk to tail, while it is up.
    const tr = envelope(t, 0.3, 0.42, 0.85, 1.0) * shiver(t, 9);
    b.body.rot(0.012 * tr, 0, 0.015 * tr);
    b.head.rot(0.03 * tr, 0.03 * tr);
    b.trunk2.rot(0.06 * tr);
    b.trunk3.rot(0.08 * tr);
    // The crash down jolts the wreck on its back.
    b.howdah.rot(0.1 * kick(t, 1.07, 3, 6), 0, 0.06 * kick(t, 1.1, 2.5, 6));
  }, (t) => thud(t, [[1.05, 0.05]]), 'Pánico');

export const mammothClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), panic()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { mammoth: mammothClips };
